import React from 'react';
import { BaseCalendar } from './components/BaseCalendar';
import { DividendsCard } from './components/MobileCards/DividendsCard';
import { createDividendsColumns } from './columns/dividendsColumns';
import { BaseCalendarPageProps, DividendItem } from './types';
import { applyDateFilter, applyWatchlistFilter, applyGenericFilter } from './utils/filterUtils';

const DividendsCalendarPage: React.FC<BaseCalendarPageProps> = (props) => {
  const columnWidths = [8, 8, 10, 9, 8, 8, 8, 12, 10];

  const transformData = (data: any[]) => {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.flatMap((day) => {
      if (!day || !Array.isArray(day.items)) {
        return [];
      }

      return day.items.map((item: DividendItem) => ({
        symbol: item.t,
        marketCap: item.mc,
        declarationDate: item.dd || '--',
        // The provider's ex-dividend date, kept as a plain YYYY-MM-DD day. It used to be computed
        // as the record date minus one business day, which has been a day early since US T+1
        // settlement (2024-05-28) and slipped another day west of UTC.
        date: item.d || '--',
        recordDate: item.rd || '--',
        paymentDate: item.pd || '--',
        adjDividend: item.ad || '--',
        dividend: item.div || '--',
        yield: item.y || '--',
        frequency: item.f || '--',
      }));
    });
  };

  const filterData = (transformedData: any[], filterOptions: any) => {
    const isTickerSpecific =
      filterOptions.selectedDate === null &&
      filterOptions.fromDate === null &&
      filterOptions.toDate === null &&
      filterOptions.selectedTimeframe === null;

    if (isTickerSpecific) {
      return transformedData;
    }

    let filteredItems = applyDateFilter(transformedData, filterOptions);

    if (!filterOptions.selectedThirdSectionFilters.has('all')) {
      filteredItems = applyGenericFilter(
        filteredItems,
        filterOptions.selectedThirdSectionFilters,
        'frequency',
        (item, filter) => {
          const frequency = item.frequency;
          if (!frequency || frequency === '--') {
            return filter === 'other' || filter === '--';
          }

          if (filter.toLowerCase() === frequency.toLowerCase()) return true;

          if (filter === 'annual' && frequency.toLowerCase().includes('annual')) return true;
          if (filter === 'quarterly' && frequency.toLowerCase().includes('quarterly')) return true;
          if (filter === 'monthly' && frequency.toLowerCase().includes('monthly')) return true;

          return false;
        },
      );
    }

    if (!filterOptions.selectedFourthSectionFilters.has('all')) {
      filteredItems = applyGenericFilter(
        filteredItems,
        filterOptions.selectedFourthSectionFilters,
        'yield',
        (item, filter) => {
          const yieldValue = item.yield;
          if (yieldValue == null || isNaN(yieldValue)) {
            return filter === 'none';
          }

          if (filter === 'low' && yieldValue > 0 && yieldValue <= 2) return true;
          if (filter === 'medium' && yieldValue > 2 && yieldValue <= 5) return true;
          if (filter === 'high' && yieldValue > 5) return true;

          return false;
        },
      );
    }

    return applyWatchlistFilter(
      filteredItems,
      filterOptions.isWatchListOnly,
      filterOptions.bookmarkedItems,
    );
  };

  return (
    <BaseCalendar
      {...props}
      symbol={props?.symbol ?? ''}
      screenType="dividends"
      title="Dividends Calendar"
      columns={createDividendsColumns}
      columnWidths={columnWidths}
      mobileCard={DividendsCard}
      filterData={filterData}
      transformData={transformData}
    />
  );
};

export default DividendsCalendarPage;
